import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Mail, Phone, Send, Loader2 } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

export const Contact = ({ company }) => {
    const t = useT();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
        privacy_consent: false,
    });

    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.privacy_consent) {
            toast.error(t({ de: "Bitte stimmen Sie den Datenschutzhinweisen zu.", en: "Please accept the privacy notice." }));
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/inquiries", {
                ...form,
                apartment_id: "allgemein",
                apartment_label: "Allgemeine Anfrage",
                usage_type: "unentschieden",
                financing: "unentschieden",
            });
            toast.success(t({ de: "Anfrage gesendet", en: "Request sent" }), {
                description: t({ de: "Vielen Dank! Wir melden uns zeitnah.", en: "Thank you! We will be in touch shortly." }),
            });
            setForm({ name: "", email: "", phone: "", message: "", privacy_consent: false });
        } catch (err) {
            toast.error(t({ de: "Anfrage konnte nicht gesendet werden", en: "Request could not be sent" }), {
                description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="kontakt" className="bg-white py-24 sm:py-32" data-testid="contact-section">
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="overline">{t({ de: "Kontakt", en: "Contact" })}</span>
                        <h2 className="h-section mt-4 text-balance">
                            {t({ de: "Sprechen wir", en: "Let's talk" })}
                            <span className="italic text-brand-500">
                                {t({ de: " über Ihr Zuhause.", en: " about your home." })}
                            </span>
                        </h2>
                        <p className="body-md mt-6">
                            {t({
                                de: "Sie haben eine bestimmte Wohnung im Auge oder allgemeine Fragen zum Projekt? Wir melden uns zeitnah – gern auch zu einem persönlichen Termin auf der Baustelle.",
                                en: "Have a specific apartment in mind, or general questions about the project? We'll respond quickly – including for an on-site appointment at the build location.",
                            })}
                        </p>

                        <div className="mt-10 space-y-5">
                            <ContactItem
                                icon={Phone}
                                label={t({ de: "Telefon", en: "Phone" })}
                                value={company?.phone || "+49 611 000000"}
                                href={`tel:${(company?.phone || "").replace(/\s/g, "")}`}
                                testid="contact-phone"
                            />
                            <ContactItem
                                icon={Mail}
                                label={t({ de: "E-Mail", en: "E-mail" })}
                                value={company?.email || "info@g21-wiesbaden.de"}
                                href={`mailto:${company?.email || "info@g21-wiesbaden.de"}`}
                                testid="contact-email"
                            />
                        </div>
                    </div>

                    <div className="lg:col-span-6 lg:col-start-7">
                        <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                            <div className="grid sm:grid-cols-2 gap-6">
                                <div>
                                    <label className="label-line">{t({ de: "Name *", en: "Name *" })}</label>
                                    <input
                                        type="text"
                                        required
                                        value={form.name}
                                        onChange={(e) => update("name", e.target.value)}
                                        className="input-line"
                                        placeholder={t({ de: "Vor- und Nachname", en: "First and last name" })}
                                        data-testid="contact-name"
                                    />
                                </div>
                                <div>
                                    <label className="label-line">{t({ de: "E-Mail *", en: "E-mail *" })}</label>
                                    <input
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={(e) => update("email", e.target.value)}
                                        className="input-line"
                                        placeholder={t({ de: "ihre@email.de", en: "your@email.com" })}
                                        data-testid="contact-email-input"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="label-line">{t({ de: "Telefon", en: "Phone" })}</label>
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={(e) => update("phone", e.target.value)}
                                    className="input-line"
                                    placeholder="+49 …"
                                    data-testid="contact-phone-input"
                                />
                            </div>

                            <div>
                                <label className="label-line">{t({ de: "Ihre Nachricht", en: "Your message" })}</label>
                                <textarea
                                    rows={5}
                                    value={form.message}
                                    onChange={(e) => update("message", e.target.value)}
                                    className="input-line resize-none"
                                    placeholder={t({ de: "Wie können wir helfen?", en: "How can we help?" })}
                                    data-testid="contact-message"
                                />
                            </div>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.privacy_consent}
                                    onChange={(e) => update("privacy_consent", e.target.checked)}
                                    className="mt-1 h-4 w-4 rounded-none border-stone-300 text-brand-500 focus:ring-brand-500"
                                    data-testid="contact-consent"
                                    required
                                />
                                <span className="font-body text-xs text-stone-600 leading-relaxed">
                                    {t({
                                        de: "Ich willige ein, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert werden. Mehr dazu in der ",
                                        en: "I consent to my data being stored for the purpose of handling my inquiry. More in the ",
                                    })}
                                    <a href="/datenschutz" className="underline hover:text-brand-600">
                                        {t({ de: "Datenschutzerklärung", en: "privacy policy" })}
                                    </a>
                                    .
                                </span>
                            </label>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn-primary disabled:opacity-60"
                                data-testid="contact-submit"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        {t({ de: "Wird gesendet…", en: "Sending…" })}
                                    </>
                                ) : (
                                    <>
                                        <Send size={16} />
                                        {t({ de: "Anfrage senden", en: "Send inquiry" })}
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

const ContactItem = ({ icon: Icon, label, value, href, testid }) => (
    <a
        href={href}
        className="flex items-center gap-4 p-4 rounded-2xl border border-stone-200 hover:border-brand-500 hover:bg-cream transition-colors group"
        data-testid={testid}
    >
        <div className="w-11 h-11 flex items-center justify-center bg-brand-50 text-brand-600 group-hover:bg-brand-500 group-hover:text-white transition-colors">
            <Icon size={18} strokeWidth={1.5} />
        </div>
        <div>
            <div className="overline text-stone-400">{label}</div>
            <div className="font-body text-base text-ink">{value}</div>
        </div>
    </a>
);
